import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Apple, ArrowLeft, Globe, MonitorSmartphone, Smartphone, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/lib/auth-context';
import { tempoDecorrido } from '@/lib/format';
import { supabase } from '@/lib/supabase';

interface Dispositivo {
  seq_dispositivo_push: number;
  cod_token_fcm: string;
  cod_plataforma: 'IOS' | 'ANDROID' | 'WEB';
  des_user_agent: string | null;
  ind_ativo: boolean;
  dta_criacao: string;
  dta_ultimo_uso: string;
}

const PLATAFORMA_META = {
  IOS: { icon: Apple, grad: 'from-slate-700 to-slate-900' },
  ANDROID: { icon: MonitorSmartphone, grad: 'from-emerald-500 to-teal-700' },
  WEB: { icon: Globe, grad: 'from-sky-500 to-blue-700' },
} as const;

export function DispositivosPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const dispositivos = useQuery({
    queryKey: ['dispositivos-push', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_dispositivos_push')
        .select(
          'seq_dispositivo_push, cod_token_fcm, cod_plataforma, des_user_agent, ind_ativo, dta_criacao, dta_ultimo_uso',
        )
        .eq('cod_auth_user', user!.id)
        .eq('ind_ativo', true)
        .order('dta_ultimo_uso', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Dispositivo[];
    },
    enabled: !!user,
  });

  const remover = useMutation({
    mutationFn: async (cod_token_fcm: string) => {
      const { error } = await supabase.rpc('fn_remover_dispositivo_push', {
        p_cod_token_fcm: cod_token_fcm,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Dispositivo removido');
      void qc.invalidateQueries({ queryKey: ['dispositivos-push'] });
    },
    onError: (e) => toast.error('Falha: ' + (e as Error).message),
  });

  const mascararToken = (token: string) => {
    if (token.length < 16) return token;
    return token.slice(0, 8) + '...' + token.slice(-8);
  };

  return (
    <div className="space-y-5 p-4 pb-24">
      <motion.div
        className="octa-page-header"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/perfil')}
          className="text-muted-foreground -ml-2 mb-2 h-8 px-2"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Voltar
        </Button>
        <h1>Meus dispositivos</h1>
        <p>Aparelhos que recebem notificações push. Remova os que você não usa mais.</p>
      </motion.div>

      {dispositivos.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      ) : (dispositivos.data?.length ?? 0) === 0 ? (
        <EmptyState
          variant="hero"
          icon={Smartphone}
          title="Sem dispositivos"
          description="Abra o app no celular e autorize notificações para registrar seu primeiro dispositivo."
        />
      ) : (
        <ul className="space-y-3">
          <AnimatePresence>
            {dispositivos.data!.map((d, i) => {
              const meta = PLATAFORMA_META[d.cod_plataforma] ?? {
                icon: Smartphone,
                grad: 'from-slate-500 to-slate-700',
              };
              const Icon = meta.icon;
              return (
                <motion.li
                  key={d.seq_dispositivo_push}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 12, scale: 0.95 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                >
                  <Card variant="default">
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${meta.grad} shadow-octa-sm text-white`}
                          >
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-bold">{d.cod_plataforma}</p>
                            <StatusBadge variant="success" dot pulse className="mt-0.5">
                              Ativo
                            </StatusBadge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (
                              confirm(
                                'Remover este dispositivo? Você parará de receber notificações nele.',
                              )
                            ) {
                              remover.mutate(d.cod_token_fcm);
                            }
                          }}
                          disabled={remover.isPending}
                          aria-label="Remover dispositivo"
                          className="h-9 w-9 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {d.des_user_agent && (
                        <p className="text-muted-foreground truncate text-[11px]">
                          {d.des_user_agent}
                        </p>
                      )}
                      <p className="text-muted-foreground text-[11px] tracking-wider">
                        Token: {mascararToken(d.cod_token_fcm)}
                      </p>
                      <p className="text-muted-foreground text-[11px]">
                        Registrado {tempoDecorrido(d.dta_criacao)} · Ultimo uso{' '}
                        {tempoDecorrido(d.dta_ultimo_uso)}
                      </p>
                    </CardContent>
                  </Card>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}
